import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Mail, MessageCircle, Plus, Send, Star, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { FieldWrapper } from '@/components/ui/field-wrapper';
import { Card, CardContent, CardHeader, CardTitle, CardAction } from '@/components/ui/card';
import { senderProfilesApi, type SenderProfile, type SenderProfileInput } from './api';

const EMPTY: SenderProfileInput = { profileName: '', channel: 'Email', useTls: true };

export function SenderProfilesPage() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<SenderProfileInput | null>(null);
  const [testTarget, setTestTarget] = useState<SenderProfile | null>(null);
  const [testTo, setTestTo] = useState('');

  const profilesQuery = useQuery({
    queryKey: ['sender-profiles'],
    queryFn: () => senderProfilesApi.list(),
  });

  const saveMutation = useMutation({
    mutationFn: (input: SenderProfileInput) => senderProfilesApi.save(input),
    onSuccess: () => {
      toast.success('Sender profile saved.');
      setEditing(null);
      queryClient.invalidateQueries({ queryKey: ['sender-profiles'] });
    },
    onError: (e: Error) => toast.error(e.message || 'Save failed.'),
  });

  const testMutation = useMutation({
    mutationFn: ({ id, to }: { id: string; to: string }) => senderProfilesApi.test(id, to),
    onSuccess: (res) => {
      if (res.success) toast.success('Test message delivered.');
      else toast.error(`Test failed: ${res.error}`);
      setTestTarget(null);
      setTestTo('');
    },
    onError: () => toast.error('Test request failed.'),
  });

  const profiles = profilesQuery.data ?? [];

  const startEdit = (p: SenderProfile) =>
    setEditing({
      id: p.SenderProfileID,
      profileName: p.ProfileName,
      channel: p.Channel,
      smtpServer: p.SMTPServer ?? undefined,
      smtpPort: p.SMTPPort ?? undefined,
      useTls: Boolean(p.UseTLS),
      userId: p.UserID ?? undefined,
      fromEmail: p.FromEmail ?? undefined,
      fromName: p.FromName ?? undefined,
      gatewayTextUrl: p.GatewayTextURL ?? undefined,
      gatewayCaptionUrl: p.GatewayCaptionURL ?? undefined,
      gatewayAttachmentUrl: p.GatewayAttachmentURL ?? undefined,
      gatewayKey: p.GatewayKey ?? undefined,
      isDefault: Boolean(p.IsDefault),
      status: p.Status,
    });

  const updateField = (key: keyof SenderProfileInput, value: any) => {
    setEditing(prev => prev ? { ...prev, [key]: value } : prev);
  };

  return (
    <div className="max-w-5xl space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Settings2 size={20} className="text-[#008ED3]" />
          Sender Profiles
        </h1>
        <Button onClick={() => setEditing({ ...EMPTY })} className="gap-2">
          <Plus size={16} /> New profile
        </Button>
      </div>

      {profilesQuery.isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin text-[#008ED3]" />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {profiles.map((p) => (
          <Card key={p.SenderProfileID} className="overflow-hidden hover:border-[#008ED3]/30 transition-colors">
            <CardHeader className="p-4 gap-0">
              <CardAction>
                <StatusBadge status={p.Status} />
              </CardAction>
              <CardTitle className="flex items-center gap-2 text-[15px] font-semibold text-gray-900">
                {p.Channel === 'Email' ? (
                  <Mail size={16} className="text-[#008ED3]" />
                ) : (
                  <MessageCircle size={16} className="text-green-600" />
                )}
                {p.ProfileName}
                {Boolean(p.IsDefault) && (
                  <Badge className="bg-amber-100 text-amber-700 border-0 shadow-none gap-1 ml-1 text-[10px] py-0 h-4">
                    <Star size={8} className="fill-current" /> Default
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <p className="text-[13px] text-gray-500 mb-4 line-clamp-1">
                {p.Channel === 'Email'
                  ? `${p.SMTPServer ?? '—'}:${p.SMTPPort ?? '—'} · ${p.FromEmail ?? p.UserID ?? '—'}`
                  : p.GatewayTextURL ?? '—'}
                {p.TenantOrganizationID === null && ' · Global'}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => startEdit(p)}
                  className="h-8 text-[12px]"
                >
                  Edit profile
                </Button>
                <Button
                  variant="ghost_blue"
                  size="sm"
                  onClick={() => setTestTarget(p)}
                  className="h-8 text-[12px]"
                >
                  <Send size={12} className="mr-1.5" /> Send test
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {!profilesQuery.isLoading && profiles.length === 0 && (
          <div className="col-span-2 py-12 text-center border-2 border-dashed border-gray-100 rounded-xl bg-gray-50/30">
            <p className="text-sm text-gray-400">
              No sender profiles yet. Create one to start delivering messages.
            </p>
          </div>
        )}
      </div>

      {/* editor dialog */}
      <Dialog open={Boolean(editing)} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>
              {editing?.id ? 'Edit sender profile' : 'New sender profile'}
            </DialogTitle>
            <DialogDescription>
              Configure how notifications are delivered for this channel.
            </DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="grid grid-cols-2 gap-4 py-4">
              <FieldWrapper label="Profile name" required className="col-span-2">
                <Input
                  value={editing.profileName}
                  onChange={e => updateField('profileName', e.target.value)}
                  placeholder="e.g. Primary Office 365"
                />
              </FieldWrapper>

              <FieldWrapper label="Channel">
                <Select
                  value={editing.channel}
                  disabled={Boolean(editing.id)}
                  onValueChange={val => updateField('channel', val as 'Email' | 'WhatsApp')}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Email">Email</SelectItem>
                    <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
              </FieldWrapper>

              {editing.channel === 'Email' ? (
                <>
                  <FieldWrapper label="SMTP server">
                    <Input
                      value={editing.smtpServer ?? ''}
                      onChange={e => updateField('smtpServer', e.target.value)}
                      placeholder="smtp.office365.com"
                    />
                  </FieldWrapper>
                  <FieldWrapper label="SMTP port">
                    <Input
                      type="number"
                      value={editing.smtpPort ?? ''}
                      onChange={e => updateField('smtpPort', Number(e.target.value))}
                      placeholder="587"
                    />
                  </FieldWrapper>
                  <FieldWrapper label="Username">
                    <Input
                      value={editing.userId ?? ''}
                      onChange={e => updateField('userId', e.target.value)}
                    />
                  </FieldWrapper>
                  <FieldWrapper label="Password">
                    <Input
                      type="password"
                      value={editing.password ?? ''}
                      onChange={e => updateField('password', e.target.value)}
                      placeholder={editing.id ? '(unchanged)' : ''}
                    />
                  </FieldWrapper>
                  <FieldWrapper label="From email">
                    <Input
                      value={editing.fromEmail ?? ''}
                      onChange={e => updateField('fromEmail', e.target.value)}
                    />
                  </FieldWrapper>
                  <FieldWrapper label="From name">
                    <Input
                      value={editing.fromName ?? ''}
                      onChange={e => updateField('fromName', e.target.value)}
                    />
                  </FieldWrapper>
                </>
              ) : (
                <>
                  <FieldWrapper label="Text URL" className="col-span-2">
                    <Input
                      value={editing.gatewayTextUrl ?? ''}
                      onChange={e => updateField('gatewayTextUrl', e.target.value)}
                    />
                  </FieldWrapper>
                  <FieldWrapper label="Caption URL" className="col-span-2">
                    <Input
                      value={editing.gatewayCaptionUrl ?? ''}
                      onChange={e => updateField('gatewayCaptionUrl', e.target.value)}
                    />
                  </FieldWrapper>
                  <FieldWrapper label="Attachment URL" className="col-span-2">
                    <Input
                      value={editing.gatewayAttachmentUrl ?? ''}
                      onChange={e => updateField('gatewayAttachmentUrl', e.target.value)}
                    />
                  </FieldWrapper>
                  <FieldWrapper label="Gateway key" className="col-span-2">
                    <Input
                      type="password"
                      value={editing.gatewayKey ?? ''}
                      onChange={e => updateField('gatewayKey', e.target.value)}
                    />
                  </FieldWrapper>
                </>
              )}

              <div className="col-span-2 flex items-center space-x-2 pt-2">
                <Checkbox
                  id="isDefault"
                  checked={editing.isDefault ?? false}
                  onCheckedChange={(checked) => updateField('isDefault', !!checked)}
                />
                <label
                  htmlFor="isDefault"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Default profile for this channel
                </label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => editing && saveMutation.mutate(editing)}
              disabled={!editing?.profileName || saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving…' : 'Save profile'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* test-send dialog */}
      <Dialog open={Boolean(testTarget)} onOpenChange={(open) => !open && setTestTarget(null)}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Send test message</DialogTitle>
            <DialogDescription>
              Verify that “{testTarget?.ProfileName}” is correctly configured.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <FieldWrapper
              label={testTarget?.Channel === 'Email' ? 'Recipient email' : 'Recipient phone'}
              required
            >
              <Input
                placeholder={testTarget?.Channel === 'Email' ? 'recipient@example.com' : '+91…'}
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                autoFocus
              />
            </FieldWrapper>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTestTarget(null)}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                testTarget && testMutation.mutate({ id: testTarget.SenderProfileID, to: testTo })
              }
              disabled={!testTo || testMutation.isPending}
            >
              {testMutation.isPending ? (
                <>
                  <Loader2 size={14} className="animate-spin mr-2" />
                  Sending…
                </>
              ) : (
                'Send test'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
